import { Component, inject, OnInit, signal } from "@angular/core";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NumbersOnlyDirective } from "../../../../../../shared/src/lib/directives/numbers-only.directive";
import { BalanceService } from "../../../../../../shared/src/lib/services/balance.service";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { WhiteLabelConfigService } from "../../../../../../shared/src/lib/services/white-label-config.service";
import { ApiService } from "../../../../../../shared/src";
import { MathUtils } from "../../../../../../shared/src";
import { Observable, combineLatest, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { MatProgressBar } from "@angular/material/progress-bar";

const EXCHANGE = "main_to_secondary";
const CASINO = "secondary_to_main";

@Component({
    selector: "app-wallet-transfer",
    standalone: true,
    imports: [DecimalPipe, MatIconModule, ReactiveFormsModule, NumbersOnlyDirective, AsyncPipe, NgClass, MatProgressBar],
    templateUrl: "./wallet-transfer.component.html",
    styleUrl: "./wallet-transfer.component.scss",
})
export class WalletTransferComponent implements OnInit {
    readonly destroy$ = new Subject<void>();
    balanceService = inject(BalanceService);
    formBuilder = inject(FormBuilder);
    loader = inject(LoaderService);
    wlConfig = inject(WhiteLabelConfigService);
    apiService = inject(ApiService);

    form!: FormGroup;
    isLoading$!: Observable<boolean>;
    exchangeBalance = 0;
    exchangeRawBalance = 0;
    exchangeExposure = 0;
    casinoBalance = 0;
    casinoExposure = 0;
    noticeTimeout: any;
    transferNotice = signal<"success" | "error" | null>(null);
    errorMessage = signal<string>('');
    passwordVisible = signal<boolean>(false);

    get isExchange() {
        return this.transferTypeCtrl?.value === EXCHANGE;
    }

    get fromDescription() {
        return this.isExchange ? "Exchange" : "Casino";
    }

    get toDescription() {
        return this.isExchange ? "Casino" : "Exchange";
    }

    get isBalanceInsufficient() {
        const amount = this.selectedAmount;
        return amount > this.fromBalance;
    }

    get fromBalance() {
        return this.isExchange ? this.exchangeBalance : this.casinoBalance;
    }

    get toBalance() {
        return this.isExchange ? this.casinoBalance : this.exchangeBalance;
    }

    get selectedAmount() {
        return Number(this.amountCtrl?.value);
    }

    get amountCtrl() {
        return this.form.get("AMOUNT");
    }

    get transferTypeCtrl() {
        return this.form.get("TRANSFER_TYPE");
    }

    get isCasinoDisabled(): boolean {
        return this.wlConfig.showFeature("disableCasino") || this.apiService.getUserToken()?.user?.IS_BLOCKED_CASINO === "true";
    }

    get isTransferInvalid() {
        return this.form.invalid || this.isBalanceInsufficient || this.selectedAmount <= 0 || this.isCasinoDisabled;
    }

    constructor() {
        this.form = this.formBuilder.group({
            AMOUNT: [null, [Validators.required, Validators.min(1)]],
            TRANSFER_TYPE: EXCHANGE,
            PASSWORD: ["", Validators.required],
        });
    }

    ngOnInit() {
        this.balanceService.fetchBalance();

        this.isLoading$ = combineLatest([this.loader.isLoading("/user/wallets"), this.loader.isLoading("/internal-transfer")]).pipe(
            takeUntil(this.destroy$),
            map(([fetchingBalance, transferringBalance]) => fetchingBalance || transferringBalance),
        );

        this.balanceService.balanceData$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result) {
                const balance = MathUtils.roundDown(+result?.BALANCE, 2);
                const totalExposure = +result?.TOTAL_EXPOSURE;
                this.exchangeRawBalance = balance;
                this.exchangeExposure = totalExposure;
                this.exchangeBalance = MathUtils.roundDown(balance + totalExposure, 2);
            }
        });

        this.balanceService.casinoBalance$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result?.BALANCE) {
                this.casinoBalance = Number(result?.BALANCE);
                this.casinoExposure = Number(result?.TOTAL_EXPOSURE ?? 0);
            }
        });

        this.transferTypeCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            // Validate amount after swapping
            if (this.amountCtrl?.value && this.selectedAmount > this.fromBalance) {
                this.form.patchValue({ AMOUNT: this.fromBalance }, { emitEvent: false });
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    swapWallets() {
        const newValue = this.isExchange ? CASINO : EXCHANGE;
        this.form.patchValue({ TRANSFER_TYPE: newValue });
    }

    onFromWalletChange(event: Event) {
        const target = event.target as HTMLSelectElement;
        this.form.patchValue({ TRANSFER_TYPE: target.value });
    }

    onToWalletChange(event: Event) {
        const target = event.target as HTMLSelectElement;
        const newValue = target.value === EXCHANGE ? CASINO : EXCHANGE;
        this.form.patchValue({ TRANSFER_TYPE: newValue });
    }

    setMax() {
        this.form.patchValue({ AMOUNT: this.fromBalance });
        this.amountCtrl?.markAsTouched();
        this.amountCtrl?.markAsDirty();
    }

    adjustAmount(type: "increase" | "decrease") {
        const amount = this.selectedAmount || 0;
        const newAmount = type === "increase" ? amount + 1 : amount - 1;
        if (newAmount > 0 && newAmount <= this.fromBalance) {
            this.form.patchValue({ AMOUNT: newAmount });
            this.amountCtrl?.markAsTouched();
            this.amountCtrl?.markAsDirty();
        }
    }

    handleSubmit() {
        if (this.isTransferInvalid) return;

        this.balanceService
            .transferBalance(this.form.value)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response?.status === "ok") {
                        this.transferNotice.set("success");
                    } else {
                        const rawArr: string[] = Array.isArray(response) ? (response as string[]) : Array.isArray((response as unknown as { result?: string[] })?.result) ? (response as unknown as { result: string[] }).result : [];
                        const raw: string = (response as unknown as { message?: string })?.message || rawArr[0] || "Transfer failed";
                        const match = raw.match(/ERROR:\s*(.+)/i);
                        this.errorMessage.set(match ? `Transfer failed: ${match[1].trim()}` : raw);
                        this.transferNotice.set("error");
                    }
                    this.balanceService.fetchBalance();
                    this.resetNotice();
                },
                error: (error) => {
                    const raw = error?.error?.message || error?.error?.[0] || "";
                    const match = typeof raw === "string" ? raw.match(/ERROR:\s*(.+)/i) : null;
                    this.errorMessage.set(match ? `Transfer failed: ${match[1].trim()}` : raw || "Transfer failed. Please try again.");
                    this.transferNotice.set("error");
                    this.resetNotice();
                },
            });
    }

    resetNotice() {
        this.noticeTimeout = setTimeout(() => {
            this.transferNotice.set(null);
            this.resetForm();
        }, 5000);
    }

    closeNotice() {
        clearTimeout(this.noticeTimeout);
        this.transferNotice.set(null);
        this.errorMessage.set('');
        this.resetForm();
    }

    togglePassword() {
        this.passwordVisible.set(!this.passwordVisible());
    }

    resetForm() {
        this.form.patchValue({ AMOUNT: null, PASSWORD: "" });
        this.amountCtrl?.markAsUntouched();
        this.amountCtrl?.markAsPristine();
    }
}
